import React, { useState, useEffect, useRef } from "react"
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Alert,
  Badge,
  Table,
  Spinner,
  Button,
  Collapse,
  Progress,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap"
import classnames from "classnames"

// APIs
import { consultarBCRA, consultarAML } from "api"

// Componente Nosis
import SeccionNosis from "./SeccionNosis"

// ============================================
// Componentes auxiliares
// ============================================

const RiskBadge = ({ nivel }) => {
  const config = {
    BAJO: { color: "success", text: "Bajo" },
    MEDIO: { color: "warning", text: "Medio" },
    ALTO: { color: "danger", text: "Alto" },
    CRITICO: { color: "dark", text: "Crítico" },
    CLEAR: { color: "success", text: "Sin coincidencias" },
    MATCH: { color: "danger", text: "Coincidencia" },
    REVIEW: { color: "warning", text: "Requiere revisión" },
  }
  const cfg = config[nivel] || { color: "secondary", text: nivel || "N/A" }
  return <Badge color={cfg.color}>{cfg.text}</Badge>
}

const StatusIcon = ({ ok }) => {
  if (ok === null || ok === undefined) {
    return <i className="bx bx-minus text-muted" />
  }
  return ok ? (
    <i className="bx bx-check-circle text-success" />
  ) : (
    <i className="bx bx-x-circle text-danger" />
  )
}

const LoadingCard = ({ mensaje }) => (
  <Card className="mb-3">
    <CardBody className="text-center py-4">
      <Spinner color="primary" />
      <p className="mt-2 mb-0 text-muted">{mensaje}</p>
    </CardBody>
  </Card>
)

// ============================================
// Sección BCRA
// ============================================

const SeccionBCRA = ({ resultado, loading }) => {
  const [detalleAbierto, setDetalleAbierto] = useState(false)

  if (loading) {
    return <LoadingCard mensaje="Consultando Central de Deudores BCRA..." />
  }

  if (!resultado) return null

  const {
    consultaExitosa,
    peorSituacion,
    descripcionSituacion,
    deudaTotal,
    cantidadEntidades,
    diasMaxAtraso,
    tieneRefinanciaciones,
    tieneProcesoJudicial,
    detalleEntidades,
    mensajeError,
  } = resultado

  const getSituacionColor = sit => {
    if (!sit || sit <= 1) return "success"
    if (sit === 2) return "warning"
    return "danger"
  }

  return (
    <Card className="mb-3">
      <CardHeader className="bg-light d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <i
            className="bx bx-bank me-2 text-primary"
            style={{ fontSize: 20 }}
          />
          <strong>Central de Deudores - BCRA</strong>
        </div>
        {consultaExitosa ? (
          <Badge color={getSituacionColor(peorSituacion)}>
            Situación {peorSituacion || 0}:{" "}
            {descripcionSituacion || "Sin información"}
          </Badge>
        ) : (
          <Badge color="warning">No disponible</Badge>
        )}
      </CardHeader>
      <CardBody>
        {!consultaExitosa ? (
          <Alert color="warning" className="mb-0">
            <i className="bx bx-error me-2" />
            {mensajeError ||
              "No se pudo obtener información de BCRA. Requiere verificación manual."}
          </Alert>
        ) : (
          <>
            {/* Métricas principales */}
            <Row className="mb-3">
              <Col md={3} className="text-center">
                <div className="border rounded p-3">
                  <h4 className="mb-1">
                    ${(deudaTotal || 0).toLocaleString("es-AR")}
                  </h4>
                  <small className="text-muted">Deuda Total</small>
                </div>
              </Col>
              <Col md={3} className="text-center">
                <div className="border rounded p-3">
                  <h4 className="mb-1">{cantidadEntidades || 0}</h4>
                  <small className="text-muted">Entidades</small>
                </div>
              </Col>
              <Col md={3} className="text-center">
                <div className="border rounded p-3">
                  <h4
                    className={`mb-1 ${diasMaxAtraso > 30 ? "text-danger" : ""}`}
                  >
                    {diasMaxAtraso || 0} días
                  </h4>
                  <small className="text-muted">Máx. Atraso</small>
                </div>
              </Col>
              <Col md={3} className="text-center">
                <div className="border rounded p-3">
                  <h4 className="mb-1">
                    <StatusIcon ok={!tieneProcesoJudicial} />
                  </h4>
                  <small className="text-muted">Sin Proceso Judicial</small>
                </div>
              </Col>
            </Row>

            {/* Indicadores adicionales */}
            <div className="d-flex gap-4 mb-3">
              <div className="d-flex align-items-center">
                <StatusIcon ok={!tieneRefinanciaciones} />
                <span className="ms-2 small">Sin refinanciaciones</span>
              </div>
            </div>

            {/* Detalle colapsable */}
            {detalleEntidades && detalleEntidades.length > 0 && (
              <>
                <Button
                  color="link"
                  size="sm"
                  onClick={() => setDetalleAbierto(!detalleAbierto)}
                  className="p-0"
                >
                  <i
                    className={`bx bx-chevron-${detalleAbierto ? "up" : "down"}`}
                  />{" "}
                  Ver detalle por entidad ({detalleEntidades.length})
                </Button>

                <Collapse isOpen={detalleAbierto}>
                  <Table size="sm" bordered responsive className="mt-3 mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Entidad</th>
                        <th>Situación</th>
                        <th className="text-end">Monto</th>
                        <th className="text-center">Días Atraso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detalleEntidades.map((ent, idx) => (
                        <tr key={idx}>
                          <td>{ent.entidad}</td>
                          <td>
                            <Badge
                              color={getSituacionColor(ent.situacion)}
                              className="me-1"
                            >
                              {ent.situacion}
                            </Badge>
                            {ent.descripcionSituacion}
                          </td>
                          <td className="text-end">
                            ${(ent.monto || 0).toLocaleString("es-AR")}
                          </td>
                          <td className="text-center">{ent.diasAtraso || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Collapse>
              </>
            )}
          </>
        )}
      </CardBody>
    </Card>
  )
}

// ============================================
// Sección AML (Listas Restrictivas)
// ============================================

const SeccionAML = ({ resultado, loading }) => {
  if (loading) {
    return <LoadingCard mensaje="Consultando listas restrictivas (AML)..." />
  }

  if (!resultado) return null

  const {
    consultaExitosa,
    nivelRiesgo,
    repet,
    listaONU,
    listaOFAC,
    pep,
    mensajeError,
  } = resultado

  const listas = [
    {
      data: repet,
      nombre: "RePET (Argentina)",
      desc: "Registro de Terrorismo",
    },
    {
      data: listaONU,
      nombre: "ONU - Consejo de Seguridad",
      desc: "Sanciones internacionales",
    },
    { data: listaOFAC, nombre: "OFAC (EE.UU.)", desc: "SDN List" },
  ]

  return (
    <Card className="mb-3">
      <CardHeader className="bg-light d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <i
            className="bx bx-shield-quarter me-2 text-primary"
            style={{ fontSize: 20 }}
          />
          <strong>Listas Restrictivas (AML)</strong>
        </div>
        <RiskBadge nivel={nivelRiesgo} />
      </CardHeader>
      <CardBody>
        {!consultaExitosa ? (
          <Alert color="warning" className="mb-0">
            <i className="bx bx-error me-2" />
            {mensajeError ||
              "Error consultando listas. Requiere verificación manual."}
          </Alert>
        ) : (
          <>
            {/* Resultado por lista */}
            {listas.map((lista, idx) => (
              <div
                key={idx}
                className={`d-flex justify-content-between align-items-center p-2 border-bottom ${
                  lista.data?.encontrado ? "bg-danger bg-opacity-10" : ""
                }`}
              >
                <div>
                  <strong>{lista.nombre}</strong>
                  <br />
                  <small className="text-muted">{lista.desc}</small>
                </div>
                <div>
                  {!lista.data?.consultada ? (
                    <Badge color="secondary">No consultada</Badge>
                  ) : lista.data?.encontrado ? (
                    <Badge color="danger">
                      <i className="bx bx-x me-1" />
                      COINCIDENCIA
                    </Badge>
                  ) : (
                    <Badge color="success">
                      <i className="bx bx-check me-1" />
                      OK
                    </Badge>
                  )}
                </div>
              </div>
            ))}

            {/* PEP */}
            {pep && (
              <div
                className={`d-flex justify-content-between align-items-center p-2 mt-2 border rounded ${
                  pep.esPEP ? "bg-warning bg-opacity-10" : ""
                }`}
              >
                <div>
                  <strong>Persona Expuesta Políticamente (PEP)</strong>
                </div>
                <div>
                  {pep.esPEP ? (
                    <div className="text-end">
                      <Badge color="warning">ES PEP</Badge>
                      {pep.tipoPEP && (
                        <small className="d-block text-muted">
                          {pep.tipoPEP}
                        </small>
                      )}
                    </div>
                  ) : (
                    <Badge color="success">No es PEP</Badge>
                  )}
                </div>
              </div>
            )}

            {/* Alerta si hay coincidencia */}
            {nivelRiesgo === "MATCH" && (
              <Alert color="danger" className="mt-3 mb-0">
                <strong>⚠️ ALERTA CRÍTICA:</strong> Se encontraron coincidencias
                en listas restrictivas. Esta operación requiere aprobación del
                Oficial de Cumplimiento.
              </Alert>
            )}
          </>
        )}
      </CardBody>
    </Card>
  )
}

// ============================================
// Componente Principal: KYC
// ============================================

const KYC = ({ cuit, denominacion, setKYCCompletado, setResultadoKYC }) => {
  const [activeTab, setActiveTab] = useState("resumen")

  // Estados BCRA
  const [loadingBCRA, setLoadingBCRA] = useState(false)
  const [resultadoBCRA, setResultadoBCRA] = useState(null)

  // Estados AML
  const [loadingAML, setLoadingAML] = useState(false)
  const [resultadoAML, setResultadoAML] = useState(null)

  // Estados Nosis
  const [resultadoNosis, setResultadoNosis] = useState(null)

  const [error, setError] = useState(null)

  const consultaRealizada = useRef(null)
  const kycNotificadoRef = useRef(false)

  // Ejecutar consultas cuando hay CUIT
  useEffect(() => {
    if (cuit && cuit !== consultaRealizada.current) {
      // Resetear el flag cuando cambia el CUIT
      kycNotificadoRef.current = false
      ejecutarConsultas()
    }
  }, [cuit])

  const ejecutarConsultas = async () => {
    if (!cuit) {
      setError("No se proporcionó CUIT para la consulta")
      return
    }

    consultaRealizada.current = cuit
    setError(null)

    // Consultar BCRA
    setLoadingBCRA(true)
    try {
      const bcra = await consultarBCRA(cuit)
      setResultadoBCRA(bcra)
    } catch (e) {
      console.error("Error BCRA:", e)
      setResultadoBCRA({ consultaExitosa: false, mensajeError: e.message })
    }
    setLoadingBCRA(false)

    // Consultar AML
    setLoadingAML(true)
    try {
      const aml = await consultarAML(cuit, denominacion)
      setResultadoAML(aml)
    } catch (e) {
      console.error("Error AML:", e)
      setResultadoAML({ consultaExitosa: false, mensajeError: e.message })
    }
    setLoadingAML(false)
  }

  // Handler para resultado de Nosis
  const handleResultadoNosis = resultado => {
    console.log("Nosis resultado recibido:", resultado)
    setResultadoNosis(resultado)
  }

  // Calcular nivel de riesgo global
  const calcularRiesgoGlobal = (bcra, aml, nosis) => {
    // Si hay match en AML, es crítico
    if (aml?.nivelRiesgo === "MATCH") return "CRITICO"

    // Si Nosis es crítico, es crítico
    if (nosis?.nivelRiesgo === "CRITICO") return "CRITICO"

    // Si BCRA tiene situación 4 o 5, es crítico
    if (bcra?.peorSituacion >= 4) return "CRITICO"

    // Si hay proceso judicial, es alto
    if (bcra?.tieneProcesoJudicial) return "ALTO"

    // Si Nosis es alto, es alto
    if (nosis?.nivelRiesgo === "ALTO") return "ALTO"

    // Si AML requiere revisión, es medio
    if (aml?.nivelRiesgo === "REVIEW") return "MEDIO"

    // Si BCRA tiene situación 2-3, es medio
    if (bcra?.peorSituacion >= 2) return "MEDIO"

    // Si Nosis es medio, es medio
    if (nosis?.nivelRiesgo === "MEDIO") return "MEDIO"

    return "BAJO"
  }

  // Notificar al padre cuando se completan TODAS las consultas
  useEffect(() => {
    const loading = loadingBCRA || loadingAML

    // Verificar que las 3 consultas estén completas
    const bcraListo = resultadoBCRA !== null
    const amlListo = resultadoAML !== null
    const nosisListo = resultadoNosis !== null
    const todosConsultados = bcraListo && amlListo && nosisListo

    console.log("KYC useEffect - Estado:", {
      bcraListo,
      amlListo,
      nosisListo,
      loading,
      kycNotificado: kycNotificadoRef.current,
    })

    // Solo notificar una vez cuando todo esté completo
    if (todosConsultados && !loading && !kycNotificadoRef.current) {
      kycNotificadoRef.current = true

      const nivelRiesgo = calcularRiesgoGlobal(
        resultadoBCRA,
        resultadoAML,
        resultadoNosis,
      )

      console.log("KYC Completo - Nivel de riesgo:", nivelRiesgo)

      // Marcar como completado (true siempre que se hayan hecho las consultas)
      // La validación de si se puede avanzar la hace checkValidateKYC en Index.js
      if (setKYCCompletado) {
        setKYCCompletado(true)
        console.log("setKYCCompletado(true) llamado")
      }

      // Enviar resultado completo al padre
      if (setResultadoKYC) {
        const resultado = {
          bcra: resultadoBCRA,
          aml: resultadoAML,
          nosis: resultadoNosis,
          nivelRiesgoGlobal: nivelRiesgo,
          scoreNosis: resultadoNosis?.score,
          fechaConsulta: new Date().toISOString(),
        }
        setResultadoKYC(resultado)
        console.log("setResultadoKYC llamado:", resultado)
      }
    }
  }, [
    resultadoBCRA,
    resultadoAML,
    resultadoNosis,
    loadingBCRA,
    loadingAML,
    setKYCCompletado,
    setResultadoKYC,
  ])

  const nivelRiesgoGlobal =
    resultadoBCRA && resultadoAML && resultadoNosis
      ? calcularRiesgoGlobal(resultadoBCRA, resultadoAML, resultadoNosis)
      : null

  const loading = loadingBCRA || loadingAML

  // Calcular progreso
  const consultasCompletadas = [
    resultadoBCRA !== null,
    resultadoAML !== null,
    resultadoNosis !== null,
  ].filter(Boolean).length
  const progresoConsultas = Math.round((consultasCompletadas / 3) * 100)

  return (
    <div>
      <h5 className="mb-3">Verificación KYC / AML</h5>
      <p className="text-muted mb-4">
        Consulta automática a Central de Deudores BCRA, listas restrictivas de
        lavado de activos y Nosis.
      </p>

      {error && (
        <Alert color="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {!cuit ? (
        <Alert color="info">
          Complete los datos del titular para ejecutar la verificación KYC/AML.
        </Alert>
      ) : (
        <>
          {/* Barra de progreso */}
          {progresoConsultas < 100 && (
            <Card className="mb-3">
              <CardBody className="py-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span>Progreso de consultas</span>
                  <span>{consultasCompletadas}/3 completadas</span>
                </div>
                <Progress value={progresoConsultas} color="primary" />
              </CardBody>
            </Card>
          )}

          {/* Resumen de riesgo global */}
          {!loading && nivelRiesgoGlobal && (
            <Card
              className="mb-3 border-2"
              style={{
                borderColor:
                  nivelRiesgoGlobal === "BAJO"
                    ? "#28a745"
                    : nivelRiesgoGlobal === "MEDIO"
                      ? "#ffc107"
                      : "#dc3545",
              }}
            >
              <CardBody className="d-flex justify-content-between align-items-center py-3">
                <div>
                  <strong>Nivel de Riesgo Global</strong>
                  <br />
                  <small className="text-muted">
                    Evaluación combinada BCRA + AML + Nosis
                  </small>
                </div>
                <div className="text-end">
                  <RiskBadge nivel={nivelRiesgoGlobal} />
                  {nivelRiesgoGlobal === "CRITICO" && (
                    <div className="text-danger small mt-1">
                      <i className="bx bx-block me-1" />
                      Operación bloqueada
                    </div>
                  )}
                  {resultadoNosis?.score && (
                    <div className="small text-muted mt-1">
                      Score Nosis: {resultadoNosis.score}
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Tabs para las diferentes secciones */}
          <Nav tabs className="mb-3">
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "resumen" })}
                onClick={() => setActiveTab("resumen")}
                style={{ cursor: "pointer" }}
              >
                Resumen
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "bcra" })}
                onClick={() => setActiveTab("bcra")}
                style={{ cursor: "pointer" }}
              >
                BCRA
                {resultadoBCRA && (
                  <Badge
                    color={
                      resultadoBCRA.peorSituacion <= 1 ? "success" : "warning"
                    }
                    className="ms-2"
                  >
                    {resultadoBCRA.peorSituacion || 0}
                  </Badge>
                )}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "aml" })}
                onClick={() => setActiveTab("aml")}
                style={{ cursor: "pointer" }}
              >
                AML
                {resultadoAML && (
                  <Badge
                    color={
                      resultadoAML.nivelRiesgo === "CLEAR"
                        ? "success"
                        : "danger"
                    }
                    className="ms-2"
                  >
                    {resultadoAML.nivelRiesgo === "CLEAR" ? "OK" : "!"}
                  </Badge>
                )}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "nosis" })}
                onClick={() => setActiveTab("nosis")}
                style={{ cursor: "pointer" }}
              >
                Nosis
                {resultadoNosis?.score && (
                  <Badge
                    color={resultadoNosis.score >= 500 ? "success" : "warning"}
                    className="ms-2"
                  >
                    {resultadoNosis.score}
                  </Badge>
                )}
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            {/* Tab Resumen */}
            <TabPane tabId="resumen">
              <Row>
                <Col md={4}>
                  <Card className="h-100">
                    <CardHeader className="bg-light py-2">
                      <small className="fw-bold">BCRA</small>
                    </CardHeader>
                    <CardBody className="text-center">
                      {loadingBCRA ? (
                        <Spinner size="sm" />
                      ) : resultadoBCRA ? (
                        <>
                          <h3
                            className={`mb-1 text-${resultadoBCRA.peorSituacion <= 1 ? "success" : "warning"}`}
                          >
                            Situación {resultadoBCRA.peorSituacion || 0}
                          </h3>
                          <small className="text-muted">
                            Deuda: $
                            {(resultadoBCRA.deudaTotal || 0).toLocaleString(
                              "es-AR",
                            )}
                          </small>
                        </>
                      ) : (
                        <span className="text-muted">Sin datos</span>
                      )}
                    </CardBody>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="h-100">
                    <CardHeader className="bg-light py-2">
                      <small className="fw-bold">AML</small>
                    </CardHeader>
                    <CardBody className="text-center">
                      {loadingAML ? (
                        <Spinner size="sm" />
                      ) : resultadoAML ? (
                        <>
                          <RiskBadge nivel={resultadoAML.nivelRiesgo} />
                          <small className="d-block text-muted mt-2">
                            Listas consultadas
                          </small>
                        </>
                      ) : (
                        <span className="text-muted">Sin datos</span>
                      )}
                    </CardBody>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="h-100">
                    <CardHeader className="bg-light py-2">
                      <small className="fw-bold">Nosis</small>
                    </CardHeader>
                    <CardBody className="text-center">
                      {resultadoNosis ? (
                        <>
                          <h3
                            className={`mb-1 text-${resultadoNosis.score >= 500 ? "success" : "warning"}`}
                          >
                            {resultadoNosis.score || "-"}
                          </h3>
                          <small className="text-muted">Score</small>
                        </>
                      ) : (
                        <Spinner size="sm" />
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </TabPane>

            {/* Tab BCRA */}
            <TabPane tabId="bcra">
              <SeccionBCRA resultado={resultadoBCRA} loading={loadingBCRA} />
            </TabPane>

            {/* Tab AML */}
            <TabPane tabId="aml">
              <SeccionAML resultado={resultadoAML} loading={loadingAML} />
            </TabPane>

            {/* Tab Nosis */}
            <TabPane tabId="nosis">
              <SeccionNosis
                cuit={cuit}
                denominacion={denominacion}
                mostrarCompleto={true}
                onResultado={handleResultadoNosis}
                autoConsultar={true}
              />
            </TabPane>
          </TabContent>

          {/* Botón reintentar */}
          {!loading && (
            <div className="text-end mt-3">
              <Button
                color="secondary"
                size="sm"
                onClick={() => {
                  consultaRealizada.current = null
                  kycNotificadoRef.current = false
                  setResultadoBCRA(null)
                  setResultadoAML(null)
                  setResultadoNosis(null)
                  ejecutarConsultas()
                }}
              >
                <i className="bx bx-refresh me-1" />
                Reintentar todas las consultas
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default KYC
